import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { subscriptionApi } from '../../api/subscriptionApi';
import SettingsWrapper from './SettingsWrapper';
import toast from 'react-hot-toast';

const AccountPage = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [deleteModal, setDeleteModal] = useState(false);
    const [deletePass, setDeletePass] = useState('');
    const [deleteErr, setDeleteErr] = useState('');
    const [loading, setLoading] = useState({ logout: false, delete: false });

    const handleLogoutAll = async () => {
        setLoading(l => ({ ...l, logout: true }));
        try { await subscriptionApi.logoutAll(); logout(); navigate('/login'); }
        catch { toast.error('Error'); } finally { setLoading(l => ({ ...l, logout: false })); }
    };

    const handleDelete = async () => {
        if (!deletePass) { setDeleteErr('Password required'); return; }
        setLoading(l => ({ ...l, delete: true }));
        try {
            const res = await subscriptionApi.deleteAccount(user?.email || '', deletePass);
            if (res.success) { logout(); navigate('/'); }
            else setDeleteErr(res.message || 'Incorrect password');
        } catch { setDeleteErr('Error'); } finally { setLoading(l => ({ ...l, delete: false })); }
    };

    return (
        <SettingsWrapper title="Account" subtitle="Manage your account settings">
            <div className="space-y-3">
                {/* Info */}
                <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-5">
                    <h2 className="text-sm font-bold text-gray-800 mb-3">Account Information</h2>
                    <div className="space-y-2">
                        {[{ label: 'Name', value: user?.fullName }, { label: 'Email', value: user?.email }, { label: 'Plan', value: user?.currentPlan || 'Basic', teal: true }].map(({ label, value, teal }) => (
                            <div key={label} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                                <span className="text-xs text-gray-500">{label}</span>
                                <span className={`text-xs font-semibold ${teal ? 'text-teal-600' : 'text-gray-800'}`}>{value}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Logout all */}
                <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-5 flex items-start justify-between gap-4">
                    <div>
                        <p className="text-sm font-bold text-gray-800">Logout All Devices</p>
                        <p className="text-xs text-gray-400 mt-0.5">Sign out from all browsers and devices</p>
                    </div>
                    <button onClick={handleLogoutAll} disabled={loading.logout}
                        className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-all disabled:opacity-60 flex-shrink-0">
                        {loading.logout ? <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"/> :
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>}
                        Log Out All
                    </button>
                </div>

                {/* Delete */}
                <div className="bg-white border border-red-100 rounded-xl shadow-sm p-5 flex items-start justify-between gap-4">
                    <div>
                        <p className="text-sm font-bold text-red-600">Delete Account</p>
                        <p className="text-xs text-gray-400 mt-0.5">Permanently delete account and all data</p>
                    </div>
                    <button onClick={() => setDeleteModal(true)}
                        className="flex items-center gap-1.5 px-4 py-2 bg-red-50 border border-red-200 text-red-600 text-sm font-bold rounded-xl hover:bg-red-100 flex-shrink-0">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                        Delete
                    </button>
                </div>
            </div>

            {deleteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
                        <h3 className="text-base font-bold text-gray-900 text-center mb-1">Delete Account?</h3>
                        <p className="text-sm text-gray-500 text-center mb-5">This cannot be undone.</p>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Confirm password</label>
                            <input type="password" value={deletePass} onChange={e => { setDeletePass(e.target.value); setDeleteErr(''); }} onKeyDown={e => e.key === 'Enter' && handleDelete()} placeholder="Your password"
                                className={`w-full px-4 py-3 text-sm border rounded-xl focus:outline-none focus:border-teal-400 ${deleteErr ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}/>
                            {deleteErr && <p className="text-red-500 text-xs mt-1">{deleteErr}</p>}
                        </div>
                        <div className="flex gap-3">
                            <button onClick={() => { setDeleteModal(false); setDeletePass(''); setDeleteErr(''); }} className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl">Cancel</button>
                            <button onClick={handleDelete} disabled={loading.delete} className="flex-1 py-2.5 bg-red-500 text-white text-sm font-bold rounded-xl disabled:opacity-60 flex items-center justify-center gap-2">
                                {loading.delete && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>} Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </SettingsWrapper>
    );
};

export default AccountPage;